/**
 * ProofStatusBadge — read-only pill showing the embedded-proof status
 * of a file or GI entity.
 *
 * Pure presentational. No side effects, no network calls.
 */

import { cn } from '@/utils/cn';

export type ProofStatus = 'VALID' | 'OUTDATED' | 'INVALID' | 'NONE' | 'PENDING';

export interface ProofStatusBadgeProps {
  status: ProofStatus;
  size?: 'sm' | 'md';
  showLabel?: boolean;
}

const CONFIG: Record<
  ProofStatus,
  { classes: string; icon: string; label: string }
> = {
  VALID: {
    classes: 'bg-success/10 text-success',
    icon: '✓',
    label: 'Verified',
  },
  OUTDATED: {
    classes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    icon: '⚠',
    label: 'Outdated',
  },
  INVALID: {
    classes: 'bg-danger/10 text-danger',
    icon: '✗',
    label: 'Invalid',
  },
  NONE: {
    classes: 'bg-surface-raised text-ink-subtle',
    icon: '◦',
    label: 'No Proof',
  },
  PENDING: {
    classes: 'bg-spark-soft text-spark',
    icon: '⟳',
    label: 'Checking…',
  },
};

export function ProofStatusBadge({
  status,
  size = 'md',
  showLabel = true,
}: ProofStatusBadgeProps) {
  const { classes, icon, label } = CONFIG[status];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        size === 'sm'
          ? 'px-1.5 py-0.5 text-[10px]'
          : 'px-2 py-0.5 text-xs',
        status === 'PENDING' ? 'animate-pulse' : '',
        classes,
      )}
      aria-label={`Proof status: ${label}`}
    >
      <span aria-hidden="true">{icon}</span>
      {showLabel ? <span>{label}</span> : null}
    </span>
  );
}
